/**
 * AzureControlsPage - Page "NIS Azure" : dashboard + liste des 40 controles
 * Azure du catalogue, scan a la demande, score consolide par famille.
 *
 * - En tete : connection active + bouton "Scanner maintenant"
 * - Score hero : score global Azure + par famille (radar 7 familles)
 * - Tableau filtrable des 40 checks avec leur statut du dernier scan
 *
 * Pattern symetrique a ControlsPage M365 mais cible sur les ressources cloud
 * Azure (subscription unique selectionnee dans le wizard).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Card, Title2, Title3, Body1, Caption1, Button, Spinner, Badge, ProgressBar,
  Dropdown, Option,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  MessageBar, MessageBarBody, MessageBarTitle,
  tokens,
} from '@fluentui/react-components';
import {
  Play24Regular, CheckmarkCircle24Filled, ErrorCircle24Filled,
  Settings20Regular, Open16Regular, ChevronRight20Regular, ArrowDownload20Regular,
  Organization20Regular,
} from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { SeverityBadge } from '../components/StatusBadge';
import { FrameworkInlineText } from '../components/FrameworkBadge';
import { azureConnectionsApi, type AzureConnection } from '../api/azureConnectionsApi';
import { azureScansApi, type AzureScan, type AzureCheckStatus } from '../api/azureScansApi';
import {
  AZURE_CONTROLS,
  AZURE_FAMILY_ORDER,
  AZURE_FAMILY_COLORS,
  type AzureFamily,
} from '../domain/azureControls';

type StatusFilter = 'all' | AzureCheckStatus;

export function AzureControlsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [connections, setConnections]       = useState<AzureConnection[]>([]);
  const [selectedConnId, setSelectedConnId] = useState<string | null>(null);
  const [scan, setScan]                     = useState<AzureScan | null>(null);
  const [loading, setLoading]               = useState(true);
  const [scanning, setScanning]             = useState(false);
  const [error, setError]                   = useState<string | null>(null);

  // Filtres
  const [familyFilter, setFamilyFilter]   = useState<AzureFamily | 'all'>('all');
  const [statusFilter, setStatusFilter]   = useState<StatusFilter>('all');
  const [severityFilter, setSeverityFilter] = useState<'all'|'critical'|'high'|'medium'|'low'>('all');

  // ---------------------- Loaders ----------------------
  useEffect(() => { void loadConnections(); }, []);

  useEffect(() => {
    if (!selectedConnId) return;
    void loadLatestScan(selectedConnId);
  }, [selectedConnId]);

  async function loadConnections() {
    setLoading(true);
    setError(null);
    try {
      const { connections: cs } = await azureConnectionsApi.list();
      setConnections(cs ?? []);
      if (cs && cs.length > 0 && !selectedConnId) {
        setSelectedConnId(cs[0].id);
      }
    } catch (e) {
      const err = e as Error & { status?: number };
      if (err.status !== 404) setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadLatestScan(connectionId: string) {
    try {
      const s = await azureScansApi.latest(connectionId);
      setScan(s);
    } catch (e) {
      const err = e as Error & { status?: number };
      if (err.status !== 404) setError(err.message);
      setScan(null);
    }
  }

  async function triggerScan() {
    if (!selectedConnId) return;
    setScanning(true);
    setError(null);
    try {
      const { scanId } = await azureScansApi.trigger(selectedConnId);
      // Poll toutes les 3s jusqu a status=done|error
      let attempts = 0;
      const maxAttempts = 200; // ~10 min max
      const poll = async (): Promise<void> => {
        attempts++;
        const s = await azureScansApi.get(scanId);
        setScan(s);
        if (s.status === 'done' || s.status === 'error') {
          setScanning(false);
          return;
        }
        if (attempts >= maxAttempts) {
          setScanning(false);
          setError(t('nisAzure.scanTimeout'));
          return;
        }
        setTimeout(() => void poll(), 3000);
      };
      void poll();
    } catch (e) {
      setError((e as Error).message);
      setScanning(false);
    }
  }

  // ---------------------- Computed ----------------------
  const resultByControlId = useMemo(() => {
    const map = new Map<string, AzureCheckStatus>();
    scan?.results.forEach((r) => map.set(r.controlId, r.status));
    return map;
  }, [scan]);

  // Message detaille par controle (raison du pass/fail, ou l erreur du check).
  const messageByControlId = useMemo(() => {
    const map = new Map<string, string>();
    scan?.results.forEach((r) => { if (r.message) map.set(r.controlId, r.message); });
    return map;
  }, [scan]);

  const familyScores = useMemo(() => {
    const out: Record<AzureFamily, { total: number; pass: number }> = {
      identity: { total: 0, pass: 0 }, network: { total: 0, pass: 0 },
      data: { total: 0, pass: 0 },     compute: { total: 0, pass: 0 },
      keyvault: { total: 0, pass: 0 }, logging: { total: 0, pass: 0 },
      governance: { total: 0, pass: 0 },
    };
    AZURE_CONTROLS.forEach((c) => {
      const status = resultByControlId.get(c.id);
      if (!status || status === 'manual' || status === 'not_applicable') return;
      out[c.family].total++;
      if (status === 'pass') out[c.family].pass++;
    });
    return out;
  }, [resultByControlId]);

  const globalScore = useMemo(() => {
    if (!scan) return null;
    const scored = scan.results.filter((r) => r.score != null && r.status !== 'manual' && r.status !== 'not_applicable');
    if (scored.length === 0) return 0;
    const sum = scored.reduce((s, r) => s + (r.score ?? 0), 0);
    return Math.round(100 * sum / scored.length);
  }, [scan]);

  const filtered = useMemo(() => {
    return AZURE_CONTROLS.filter((c) => {
      if (familyFilter !== 'all' && c.family !== familyFilter) return false;
      if (severityFilter !== 'all' && c.severity !== severityFilter) return false;
      if (statusFilter !== 'all') {
        const st = resultByControlId.get(c.id);
        if (st !== statusFilter) return false;
      }
      return true;
    });
  }, [familyFilter, severityFilter, statusFilter, resultByControlId]);

  // Export CSV des resultats du scan Azure (cote client, separateur ';' + BOM).
  function handleDownloadCsv() {
    if (!scan) return;
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = [
      t('controls.list.id'),
      t('controls.table.family'),
      t('controls.list.control'),
      t('controls.table.severity'),
      t('controls.filter.status'),
      t('scans.detail.evidence'),
    ];
    const lines = AZURE_CONTROLS.map((c) => [
      c.id,
      t(`nisAzure.family.${c.family}`),
      c.title,
      c.severity,
      resultByControlId.get(c.id) ?? 'not_scanned',
      messageByControlId.get(c.id) ?? '',
    ]);
    const csv = [header, ...lines].map((cols) => cols.map(esc).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scan_azure_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // ---------------------- Render ----------------------
  if (loading) {
    return <Spinner label={t('nisAzure.loading')} />;
  }

  // Aucune connection Azure -> CTA wizard
  if (connections.length === 0) {
    return (
      <div>
        <PageHeader title={t('nisAzure.title')} description={t('nisAzure.subtitle')} />
        <Card style={{ padding: 40, textAlign: 'center' }}>
          <div style={{
            width: 80, height: 80, borderRadius: 20, background: '#0078D4',
            color: '#fff', fontSize: 28, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>AZ</div>
          <Title2 style={{ marginBottom: 8 }}>{t('nisAzure.empty.title')}</Title2>
          <Body1 style={{ display: 'block', marginBottom: 20, color: tokens.colorNeutralForeground2 }}>
            {t('nisAzure.empty.body')}
          </Body1>
          <Button appearance="primary" onClick={() => navigate('/tenants')}>
            {t('nisAzure.empty.cta')}
          </Button>
        </Card>
      </div>
    );
  }

  const selectedConn = connections.find((c) => c.id === selectedConnId);

  return (
    <div>
      <style>{`
        tr[data-nis365-clickable-row] { cursor: pointer; transition: background-color 0.12s ease; }
        tr[data-nis365-clickable-row]:hover { background-color: ${tokens.colorNeutralBackground1Hover}; }
        tr[data-nis365-clickable-row]:hover .nis365-row-chevron { opacity: 1; transform: translateX(2px); }
        .nis365-row-chevron { opacity: 0.35; transition: opacity 0.15s ease, transform 0.15s ease; color: ${tokens.colorBrandForeground1}; }
      `}</style>
      <PageHeader
        title={t('nisAzure.title')}
        description={t('nisAzure.subtitle')}
        actions={
          <>
            {/* Schema d architecture : releve par le meme scan (RunAzureScan etape 6). */}
            <Button
              appearance="subtle"
              icon={<Organization20Regular />}
              onClick={() => navigate('/azure/schema')}
            >
              {t('nisAzure.openDiagram', 'Faire le schéma d’Azure')}
            </Button>
            <Button
              appearance="subtle"
              icon={<ArrowDownload20Regular />}
              onClick={handleDownloadCsv}
              disabled={!scan}
            >
              {t('nisAzure.downloadCsv', 'Télécharger le résultat (CSV)')}
            </Button>
            {connections.length > 1 && (
              <Dropdown
                size="small"
                placeholder={t('nisAzure.connectionPicker')}
                value={selectedConn?.name ?? ''}
                selectedOptions={selectedConnId ? [selectedConnId] : []}
                onOptionSelect={(_, d) => setSelectedConnId(d.optionValue ?? null)}
                style={{ minWidth: 240 }}
              >
                {connections.map((c) => <Option key={c.id} value={c.id}>{c.name}</Option>)}
              </Dropdown>
            )}
          </>
        }
      />

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Filtres + tableau */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t('controls.list.id')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.table.family')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.list.control')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.table.severity')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.filter.status')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.list.references')}</TableHeaderCell>
              <TableHeaderCell style={{ width: 60 }}></TableHeaderCell>
            </TableRow>
            <TableRow>
              <TableHeaderCell />
              <TableHeaderCell>
                <Dropdown
                  size="small"
                  value={familyFilter === 'all' ? t('nisAzure.filter.allFamilies') : t(`nisAzure.family.${familyFilter}`)}
                  selectedOptions={[familyFilter]}
                  onOptionSelect={(_, d) => setFamilyFilter((d.optionValue as AzureFamily | 'all') ?? 'all')}
                  style={{ minWidth: 100 }}
                >
                  <Option value="all">{t('nisAzure.filter.allFamilies')}</Option>
                  {AZURE_FAMILY_ORDER.map((f) => <Option key={f} value={f}>{t(`nisAzure.family.${f}`)}</Option>)}
                </Dropdown>
              </TableHeaderCell>
              <TableHeaderCell />
              <TableHeaderCell>
                <Dropdown
                  size="small"
                  value={severityFilter === 'all' ? t('controls.filter.allF') : t(`severity.${severityFilter}`)}
                  selectedOptions={[severityFilter]}
                  onOptionSelect={(_, d) => setSeverityFilter((d.optionValue as 'all'|'critical'|'high'|'medium'|'low') ?? 'all')}
                  style={{ minWidth: 100 }}
                >
                  <Option value="all">{t('controls.filter.allF')}</Option>
                  <Option value="critical">{t('severity.critical')}</Option>
                  <Option value="high">{t('severity.high')}</Option>
                  <Option value="medium">{t('severity.medium')}</Option>
                  <Option value="low">{t('severity.low')}</Option>
                </Dropdown>
              </TableHeaderCell>
              <TableHeaderCell>
                <Dropdown
                  size="small"
                  value={statusFilter === 'all' ? t('controls.filter.allM') : t(`status.${statusFilter}`)}
                  selectedOptions={[statusFilter]}
                  onOptionSelect={(_, d) => setStatusFilter((d.optionValue as StatusFilter) ?? 'all')}
                  style={{ minWidth: 110 }}
                >
                  <Option value="all">{t('controls.filter.allM')}</Option>
                  <Option value="pass">{t('status.pass')}</Option>
                  <Option value="warning">{t('status.warning')}</Option>
                  <Option value="fail">{t('status.fail')}</Option>
                  <Option value="manual">{t('status.manual')}</Option>
                  <Option value="error">{t('status.error')}</Option>
                </Dropdown>
              </TableHeaderCell>
              <TableHeaderCell />
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => {
              const st = resultByControlId.get(c.id);
              const col = AZURE_FAMILY_COLORS[c.family];
              return (
                <TableRow
                  key={c.id}
                  data-nis365-clickable-row
                  onClick={() => navigate(`/azure/${c.id}`)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/azure/${c.id}`); } }}
                >
                  <TableCell><span style={{ color: tokens.colorBrandForeground1, fontWeight: 600 }}>{c.id}</span></TableCell>
                  <TableCell>
                    <Badge appearance="filled" style={{ background: col.bg, color: col.fg, fontWeight: 600 }}>
                      {t(`nisAzure.family.${c.family}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Body1 style={{ fontWeight: 600 }}>{c.title}</Body1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                      {c.description.slice(0, 100)}{c.description.length > 100 ? '…' : ''}
                    </Caption1>
                  </TableCell>
                  <TableCell><SeverityBadge severity={c.severity} /></TableCell>
                  <TableCell>
                    <AzureStatusBadge status={st} />
                    {st && st !== 'manual' && st !== 'not_applicable' && messageByControlId.get(c.id) && (
                      <Caption1 style={{
                        display: 'block', marginTop: 4, fontSize: 11,
                        color: (st === 'error' || st === 'fail') ? '#B82329' : (st === 'warning' ? '#A86317' : tokens.colorNeutralForeground3),
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {messageByControlId.get(c.id)}
                      </Caption1>
                    )}
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {[...c.references].sort((a, b) => (b.framework === 'CyFun' ? 1 : 0) - (a.framework === 'CyFun' ? 1 : 0)).slice(0, 3).map((r, i) => <FrameworkInlineText key={i} reference={r} />)}
                    </div>
                  </TableCell>
                  <TableCell style={{ width: 32, textAlign: 'right' }}>
                    <ChevronRight20Regular className="nis365-row-chevron" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============================================================================
//                              SUB-COMPONENTS
// ============================================================================
function AzureStatusBadge({ status }: { status?: AzureCheckStatus }) {
  const { t } = useTranslation();
  if (!status) {
    return <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('controls.list.notScanned')}</Caption1>;
  }
  const palette: Record<AzureCheckStatus, { fg: string; bg: string }> = {
    pass:           { fg: '#1F7A45', bg: '#DCF0E5' },
    fail:           { fg: '#B82329', bg: '#FBE7E7' },
    warning:        { fg: '#A86317', bg: '#FBEFDD' },
    manual:         { fg: '#4A4A6E', bg: '#E5E5F0' },
    not_applicable: { fg: '#7A7A7A', bg: '#EEEEEE' },
    error:          { fg: '#7A1F23', bg: '#FBE3E5' },
  };
  const p = palette[status];
  return (
    <Badge appearance="filled" style={{ background: p.bg, color: p.fg, fontWeight: 600 }}>
      {t(`status.${status}`)}
    </Badge>
  );
}
